
import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Loader2, User, Sparkles, Mic, MicOff, Camera, Video, VideoOff } from 'lucide-react';
import { chatService } from '../services/chatService';
import { storage } from '../lib/storage';
import { User as UserType, Message } from '../types';
import { DEFAULT_STORE_ID } from '../constants';
import { Language, translations } from '../lib/i18n';
import { LiveChatService } from '../services/liveChatService';

interface FloatingChatProps {
  user: UserType | null;
  lang: Language;
}

const FloatingChat: React.FC<FloatingChatProps> = ({ user, lang }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLiveMode, setIsLiveMode] = useState(false);
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [liveTranscript, setLiveTranscript] = useState('');
  const [showCamera, setShowCamera] = useState(false);
  
  const scrollRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const liveService = useRef<LiveChatService>(new LiveChatService());
  const frameInterval = useRef<number | null>(null);
  
  const t = translations[lang];
  const customerId = user?.id || 'guest-session';
  const storeId = DEFAULT_STORE_ID;

  useEffect(() => {
    const loadChat = () => {
      const conv = chatService.getCustomerConversation(customerId, storeId);
      if (conv) setMessages(conv.messages);
    };
    loadChat();
    return chatService.onChatUpdate(loadChat);
  }, [customerId, storeId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, isOpen, liveTranscript]);

  const toggleLiveMode = async () => {
    if (isLiveMode) {
      liveService.current.stop();
      setIsLiveMode(false);
      setShowCamera(false);
      if (frameInterval.current) clearInterval(frameInterval.current);
    } else {
      const store = storage.getStoreConfig(storeId);
      const products = storage.getProducts().filter(p => p.storeId === storeId);
      
      setIsLiveMode(true);
      await liveService.current.start(store.name, products, {
        onAudioData: () => {},
        onTranscription: (text, isUser) => {
          setLiveTranscript(prev => isUser ? `You: ${text}` : `AI: ${text}`);
        },
        onInterrupted: () => setLiveTranscript('Interrupted...'),
        onError: (msg) => { alert(msg); setIsLiveMode(false); }
      });
    }
  };

  const toggleCamera = async () => {
    if (showCamera) {
      setShowCamera(false);
      if (frameInterval.current) clearInterval(frameInterval.current);
    } else {
      setShowCamera(true);
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
        if (videoRef.current) videoRef.current.srcObject = stream;
        
        frameInterval.current = window.setInterval(() => {
          if (videoRef.current && canvasRef.current && isLiveMode) {
            const canvas = canvasRef.current;
            canvas.width = 320;
            canvas.height = 240;
            const ctx = canvas.getContext('2d');
            ctx?.drawImage(videoRef.current, 0, 0, 320, 240);
            const base64 = canvas.toDataURL('image/jpeg', 0.5).split(',')[1];
            liveService.current.sendFrame(base64);
          }
        }, 1000);
      } catch (e) {
        alert("Camera permission denied.");
        setShowCamera(false);
      }
    }
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;
    const text = message;
    setMessage('');
    const store = storage.getStoreConfig(storeId);
    const products = storage.getProducts().filter(p => p.storeId === storeId);
    setIsTyping(true);
    await chatService.sendMessage(customerId, storeId, text, store.name, products);
    setIsTyping(false);
  };

  return (
    <div className="fixed bottom-8 right-8 z-[100] flex flex-col items-end">
      {isOpen && (
        <div className="w-[380px] h-[600px] bg-white rounded-[40px] shadow-2xl border border-gray-100 flex flex-col overflow-hidden mb-6 animate-in slide-in-from-bottom-10 duration-500">
          {/* Header */}
          <div className={`p-6 text-white transition-colors duration-500 ${isLiveMode ? 'bg-indigo-600' : 'bg-blue-600'}`}>
            <div className="flex justify-between items-center">
               <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center ${isLiveMode ? 'animate-pulse' : ''}`}>
                    <Sparkles className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-black text-lg">Lynq AI v2</h3>
                    <p className="text-[10px] font-bold text-blue-200 uppercase tracking-widest">
                      {isLiveMode ? 'Live Voice & Vision' : 'Always Online'}
                    </p>
                  </div>
               </div>
               <div className="flex gap-2">
                 <button onClick={toggleLiveMode} className={`p-2 rounded-xl transition-all ${isLiveMode ? 'bg-white text-indigo-600' : 'hover:bg-white/10'}`}>
                   {isLiveMode ? <Mic className="h-5 w-5" /> : <MicOff className="h-5 w-5" />}
                 </button>
                 <button onClick={() => setIsOpen(false)} className="p-2 hover:bg-white/10 rounded-full transition-all"><X className="h-5 w-5" /></button>
               </div>
            </div>
          </div>

          {/* Camera View for Visual Search */}
          {showCamera && (
            <div className="h-40 bg-black relative shrink-0">
               <video ref={videoRef} autoPlay playsInline className="w-full h-full object-cover opacity-80" />
               <div className="absolute inset-0 border-2 border-white/20 pointer-events-none flex items-center justify-center">
                  <div className="w-20 h-20 border border-white/50 rounded-xl animate-pulse"></div>
               </div>
               <canvas ref={canvasRef} className="hidden" />
               <button onClick={toggleCamera} className="absolute top-2 right-2 p-1.5 bg-black/50 text-white rounded-lg"><VideoOff className="h-4 w-4" /></button>
            </div>
          )}

          {/* Messages / Live Display */}
          <div ref={scrollRef} className="flex-grow p-6 overflow-y-auto space-y-4 bg-gray-50/50">
            {isLiveMode ? (
              <div className="h-full flex flex-col items-center justify-center text-center space-y-8 animate-in fade-in duration-700">
                 <div className="relative">
                    <div className="w-32 h-32 rounded-full border-4 border-indigo-100 flex items-center justify-center">
                       <div className="w-24 h-24 bg-indigo-600 rounded-full flex items-center justify-center shadow-2xl shadow-indigo-200 animate-bounce">
                          <Mic className="h-10 w-10 text-white" />
                       </div>
                    </div>
                    {[1,2,3].map(i => (
                      <div key={i} className="absolute inset-0 border-2 border-indigo-400 rounded-full animate-ping" style={{ animationDelay: `${i * 0.4}s`, opacity: 0.2 }}></div>
                    ))}
                 </div>
                 <div className="space-y-4">
                    <p className="text-xs font-black text-indigo-600 uppercase tracking-[0.2em]">Live Conversation</p>
                    <div className="p-6 bg-white rounded-[2rem] border border-indigo-50 shadow-sm max-w-[280px]">
                       <p className="text-sm font-bold text-gray-800 leading-relaxed italic">
                         {liveTranscript || "I'm listening, bhai. Try saying 'Show me top laptops' or hold a product to the camera."}
                       </p>
                    </div>
                    {!showCamera && (
                       <button onClick={toggleCamera} className="flex items-center gap-2 mx-auto text-[10px] font-black uppercase text-gray-400 hover:text-indigo-600 transition-colors">
                          <Camera className="h-4 w-4" /> Enable Visual AI
                       </button>
                    )}
                 </div>
              </div>
            ) : (
              <>
                {messages.length === 0 && (
                  <div className="py-10 text-center space-y-4">
                     <div className="w-16 h-16 bg-blue-100 rounded-full mx-auto flex items-center justify-center text-blue-600">
                        <MessageCircle className="h-8 w-8" />
                     </div>
                     <p className="text-gray-400 text-sm font-bold">Ask me anything!</p>
                  </div>
                )}
                {messages.map((msg) => (
                  <div key={msg.id} className={`flex ${msg.senderId === customerId ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] p-4 rounded-3xl text-sm font-medium ${
                      msg.senderId === customerId ? 'bg-blue-600 text-white rounded-tr-none' : 'bg-white border border-gray-100 text-gray-800 rounded-tl-none shadow-sm'
                    }`}>
                      {msg.isAi && <div className="flex items-center gap-1 mb-1 opacity-50 text-[10px] font-black uppercase"><Sparkles className="h-3 w-3" /> AI Assistant</div>}
                      {msg.text}
                    </div>
                  </div>
                ))}
                {isTyping && (
                  <div className="flex justify-start">
                    <div className="bg-white p-4 rounded-3xl border border-gray-100 shadow-sm rounded-tl-none flex gap-1">
                      <div className="w-1.5 h-1.5 bg-gray-300 rounded-full animate-bounce"></div>
                      <div className="w-1.5 h-1.5 bg-gray-300 rounded-full animate-bounce delay-75"></div>
                      <div className="w-1.5 h-1.5 bg-gray-300 rounded-full animate-bounce delay-150"></div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Input */}
          {!isLiveMode && (
            <form onSubmit={handleSend} className="p-6 border-t border-gray-100 bg-white">
              <div className="relative">
                <input 
                  type="text" value={message} onChange={(e) => setMessage(e.target.value)}
                  placeholder="Type your question..."
                  className="w-full pl-6 pr-14 py-4 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-blue-500 font-bold text-sm"
                />
                <button type="submit" className="absolute right-2 top-1/2 -translate-y-1/2 p-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all active:scale-90">
                  <Send className="h-4 w-4" />
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      <button 
        onClick={() => setIsOpen(!isOpen)}
        className={`w-16 h-16 rounded-[2rem] shadow-2xl flex items-center justify-center text-white hover:scale-110 active:scale-95 transition-all group ${isLiveMode ? 'bg-indigo-600' : 'bg-blue-600'}`}
      >
        {isOpen ? <X className="h-8 w-8" /> : (
          <div className="relative">
             <MessageCircle className="h-8 w-8 group-hover:rotate-12 transition-transform" />
             <div className="absolute -top-2 -right-2 bg-indigo-400 text-[8px] font-black px-1.5 py-0.5 rounded-full border-2 border-white uppercase">V2</div>
          </div>
        )}
      </button>
    </div>
  );
};

export default FloatingChat;
